import * as React from "react"
import type { ComponentType } from "react"
import {
  Bookmark,
  Ellipsis,
  Heart,
  Image as ImageIcon,
  Info,
  MessageCircle,
  Play,
  Repeat2,
  Send,
  Share,
  ThumbsUp,
  TriangleAlert,
} from "lucide-react"

import { HELP_TEXT } from "@/lib/ui-tokens"
import { cn } from "@/lib/utils"
import { NETWORKS, type NetworkSlug } from "@/lib/networks"
import { Networks } from "@/shadcn/components/ui/networks"
import { Tabs, TabsList, TabsTrigger } from "@/shadcn/components/ui/tabs"
import { SourceChip } from "./marks"
import { type ConnectedSource, type PostPreviewSpec } from "./types"

/**
 * How the post will actually look, network by network.
 *
 * `networks` is what the flow *could* post to rather than what the user picked:
 * prod shows the same four to everyone, connected accounts or not, which is
 * what lets this step run before destinations are chosen.
 *
 * One `PostPreviewSpec` drives every network — each card reads the subset it
 * needs, so the caller builds the preview once rather than per network.
 */

/* ── post preview · per-network cards (Figma 28673-2141) ───────────────── */

/** Brand-coloured avatar used by every post card. */
function PostAvatar({
  post,
  size,
  text,
}: {
  post: PostPreviewSpec
  size: string
  text: string
}) {
  // A feed's favicon and an account's profile picture are user data pointing at
  // someone else's host, so a dead one is routine rather than exceptional. Same
  // fallback chain as OutputAvatar: image, then initials.
  const [imageFailed, setImageFailed] = React.useState(false)
  const showImage = Boolean(post.avatarUrl) && !imageFailed

  return (
    <span
      className={cn(
        "tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-full tw:font-semibold tw:text-white",
        size,
        text,
        /*
         * Backdrop only under the INITIALS. It exists to carry white letters, so
         * painting it under an image is at best invisible and at worst wrong: a
         * network avatar is often a transparent PNG (Pinterest serves
         * `logo_transparent_144x144.png`), and the muted-foreground grey showed
         * straight through the logo as a grey disc.
         */
        !showImage && !post.avatarColor && "tw:bg-muted-foreground"
      )}
      // Account brand colour is backend data, not a theme decision.
      style={
        !showImage && post.avatarColor
          ? { backgroundColor: post.avatarColor }
          : undefined
      }
      aria-hidden
    >
      {showImage ? (
        <img
          src={post.avatarUrl}
          alt=""
          className="tw:size-full tw:object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        post.initials
      )}
    </span>
  )
}

/**
 * Icon + label / count pair in a post's action row.
 *
 * `verbLabel` marks a label as a restatement of the icon ("Like", "Comment")
 * rather than data, and hides it below `sm`. LinkedIn's four of them need ~244px
 * of the 263px a 303px card leaves, so ~6px lands between each label and the
 * next icon and the row reads as one run of text. Icon-only is what the
 * platforms themselves fall back to at narrow widths, and it matches the
 * Instagram card's row on this same screen.
 *
 * Counts ("24", "168") are data, never decoration, so they stay at every width.
 */
function PostAct({
  icon: Icon,
  label,
  verbLabel,
}: {
  icon: ComponentType<{ className?: string }>
  label?: string
  verbLabel?: boolean
}) {
  return (
    <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-1.5">
      <Icon className="tw:size-[18px] tw:text-muted-foreground" />
      {label ? (
        <span
          className={cn(
            "tw:text-[13px] tw:leading-4 tw:font-medium tw:text-muted-foreground",
            verbLabel && "tw:hidden tw:sm:inline"
          )}
        >
          {label}
        </span>
      ) : null}
    </div>
  )
}

/**
 * Shared media block: the item's own picture or video where there is one, and
 * the muted panel with a centred glyph where there isn't.
 *
 * The glyph is the fallback rather than the default — a preview whose whole job
 * is showing what the post will look like is a lot more convincing with the real
 * media in it, and the placeholder reads as "no image" either way.
 *
 * NOT handled: YouTube's `embedHtml`. The endpoint returns an oEmbed iframe
 * string, and rendering it would mean dangerouslySetInnerHTML on this card;
 * YouTube also returns a thumbnail, which `src` shows instead.
 */
function PostMedia({
  src,
  videoSrc,
  className,
}: {
  src?: string
  videoSrc?: string
  className?: string
}) {
  // Item media is user data on a publisher's own host, so a dead URL is routine
  // rather than exceptional. Same fallback chain as PostAvatar and FeedMedia,
  // with one more rung here: a video that won't load still has its poster.
  const [videoFailed, setVideoFailed] = React.useState(false)
  const [imageFailed, setImageFailed] = React.useState(false)
  // Latches: once the viewer has pressed play the controls stay, so pausing to
  // scrub doesn't take them away and put the big play button back.
  const [started, setStarted] = React.useState(false)
  const videoRef = React.useRef<HTMLVideoElement>(null)

  const showVideo = Boolean(videoSrc) && !videoFailed
  const showImage = !showVideo && Boolean(src) && !imageFailed

  return (
    <div
      className={cn(
        "tw:relative tw:w-full tw:overflow-hidden tw:bg-muted",
        className
      )}
    >
      {showVideo ? (
        <>
          {/* preload="metadata" so the first frame stands in as the poster:
              the networks this card imitates show a still until you press
              play, and native `controls` would put a browser chrome bar
              inside a post that is supposed to look like a post. Controls
              appear once it is playing, which is the only point they are
              useful. */}
          <video
            ref={videoRef}
            src={videoSrc}
            poster={src}
            className="tw:size-full tw:object-cover"
            preload="metadata"
            playsInline
            controls={started}
            onError={() => setVideoFailed(true)}
          />
          {started ? null : (
            <button
              type="button"
              aria-label="Play video"
              className="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center"
              onClick={() => {
                setStarted(true)
                // Rejects when the browser blocks playback; the controls the
                // click just revealed are then the way in.
                videoRef.current?.play().catch(() => {})
              }}
            >
              <span className="tw:flex tw:size-10 tw:items-center tw:justify-center tw:rounded-full tw:bg-foreground/50 tw:text-background">
                <Play className="tw:size-4 tw:fill-current" />
              </span>
            </button>
          )}
        </>
      ) : showImage ? (
        // alt="" because the headline sits next to every one of these; a
        // description here would announce the same thing twice.
        <img
          src={src}
          alt=""
          className="tw:size-full tw:object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <ImageIcon className="tw:absolute tw:top-1/2 tw:left-1/2 tw:size-8 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:text-muted-foreground" />
      )}
    </div>
  )
}

/** Facebook / LinkedIn link card. LinkedIn puts the headline above the domain. */
function PostLinkCard({
  post,
  variant,
}: {
  post: PostPreviewSpec
  variant: "facebook" | "linkedin"
}) {
  const facebook = variant === "facebook"
  return (
    <div
      className={cn(
        "tw:flex tw:w-full tw:flex-col tw:overflow-hidden",
        facebook && "tw:rounded-md tw:border tw:border-border"
      )}
    >
      {/* aspect-[2/1] rather than h-[210px]: a fluid width with a fixed height
          moved the ratio with the viewport (416x210 ~ 2:1 at the design width
          became 279x210 = 1.33:1 on a phone). 416/2 = 208, so desktop shifts 2px. */}
      <PostMedia
        src={post.mediaUrl}
        videoSrc={post.videoUrl}
        className="tw:aspect-[2/1]"
      />
      <div className="tw:flex tw:w-full tw:flex-col tw:gap-0.5 tw:bg-muted tw:px-3 tw:py-2.5">
        {facebook ? (
          <>
            <span className="tw:text-xs tw:leading-4 tw:text-muted-foreground">
              {post.linkDomain?.toUpperCase()}
            </span>
            <span className="tw:text-[15px] tw:leading-5 tw:font-semibold tw:text-foreground">
              {post.headline}
            </span>
          </>
        ) : (
          <>
            <span className="tw:text-sm tw:leading-5 tw:font-semibold tw:text-foreground">
              {post.headline}
            </span>
            <span className="tw:text-xs tw:leading-4 tw:text-muted-foreground">
              {post.linkDomain}
            </span>
          </>
        )}
      </div>
    </div>
  )
}

/** The card chrome Facebook, X and LinkedIn share. */
const POST_SHELL =
  "tw:flex tw:w-full tw:flex-col tw:items-start tw:gap-2.5 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:px-3 tw:pt-3 tw:pb-2"

/**
 * Optical size correction for the tab glyphs, in the spirit of marks.tsx's
 * SOURCE_MARK_SCALE.
 *
 * The `dlvr-networks` glyphs are drawn to a shared em box, but X's mark fills
 * far more of its box than the others do — at a common 24px it reads noticeably
 * heavier than Facebook, LinkedIn and Instagram beside it. 20px matches its
 * optical weight to the row. Add an entry only for a mark that visibly
 * disagrees with its neighbours; the default is right for everything else.
 */
const TAB_GLYPH_SIZE: Partial<Record<NetworkSlug, number>> = {
  x: 20,
}

const TAB_GLYPH_DEFAULT = 24

/**
 * A post as it appears on one network. The four Figma variants differ far
 * beyond a swapped logo — header composition, body copy, media framing and the
 * whole action row change — so each gets its own branch rather than a
 * parameterised shell. Note none of them badge the card with a network mark;
 * the tab strip above identifies the network.
 */
function PostCard({
  network,
  post,
}: {
  network: NetworkSlug
  post: PostPreviewSpec
}) {
  // No stand-in when the source gave no date: this card predicts a post that
  // has not happened, so a printed "2h" would be the one piece of it a reader
  // could mistake for a fact.
  const time = post.timeAgo

  /* ── X ─────────────────────────────────── Figma 28671-2113 (440×351) */
  if (network === "x") {
    const counts = post.counts ?? {}
    return (
      <div className={POST_SHELL}>
        <div className="tw:flex tw:w-full tw:items-center tw:gap-2">
          <PostAvatar post={post} size="tw:size-10" text="tw:text-sm" />
          <div className="tw:flex tw:min-w-0 tw:flex-col">
            <span className="tw:truncate tw:text-sm tw:leading-[18px] tw:font-semibold tw:text-foreground">
              {post.author}
            </span>
            <span className="tw:truncate tw:text-[13px] tw:leading-4 tw:text-muted-foreground">
              {[post.handle, time].filter(Boolean).join(" · ")}
            </span>
          </div>
        </div>

        {/* X folds the headline into the post copy, since it has no link card
            to put it in. Skipped when the copy already carries it: prod's
            default post text is built from the item title, so joining both
            printed the headline twice on the one network that can't separate
            them. */}
        <p className="tw:w-full tw:text-[15px] tw:leading-[21px] tw:text-foreground">
          {post.headline && !post.body.includes(post.headline)
            ? `${post.body} ${post.headline}`
            : post.body}
        </p>

        {/* 416/1.9 = 219 against Figma's 220 — a 1px change at the design
            width, and the ratio now holds at every width. */}
        <PostMedia
          src={post.mediaUrl}
          videoSrc={post.videoUrl}
          className="tw:aspect-[19/10] tw:rounded-lg tw:border tw:border-border"
        />

        <div className="tw:flex tw:w-full tw:items-start tw:justify-between tw:px-2 tw:pt-0.5">
          <PostAct icon={MessageCircle} label={counts.comments} />
          <PostAct icon={Repeat2} label={counts.reposts} />
          <PostAct icon={Heart} label={counts.likes} />
          <PostAct icon={Share} />
        </div>
      </div>
    )
  }

  /* ── LinkedIn ──────────────────────────── Figma 28673-2082 (440×419) */
  if (network === "linkedin") {
    return (
      <div className={POST_SHELL}>
        <div className="tw:flex tw:w-full tw:items-center tw:gap-2">
          <PostAvatar post={post} size="tw:size-12" text="tw:text-base" />
          <div className="tw:flex tw:min-w-0 tw:flex-col">
            <span className="tw:truncate tw:text-sm tw:leading-[18px] tw:font-semibold tw:text-foreground">
              {post.author}
            </span>
            {post.followers ? (
              <span className="tw:truncate tw:text-xs tw:leading-4 tw:text-muted-foreground">
                {post.followers}
              </span>
            ) : null}
            {time || post.edited ? (
              <span className="tw:truncate tw:text-xs tw:leading-4 tw:text-muted-foreground">
                {[time, post.edited ? "Edited" : null].filter(Boolean).join(" · ")}
              </span>
            ) : null}
          </div>
        </div>

        <p className="tw:w-full tw:text-sm tw:leading-5 tw:text-foreground">
          {post.body}
        </p>

        <PostLinkCard post={post} variant="linkedin" />

        <div className="tw:h-px tw:w-full tw:bg-border" />

        <div className="tw:flex tw:w-full tw:items-start tw:justify-between tw:px-2 tw:pt-0.5">
          <PostAct icon={ThumbsUp} label="Like" verbLabel />
          <PostAct icon={MessageCircle} label="Comment" verbLabel />
          <PostAct icon={Repeat2} label="Repost" verbLabel />
          <PostAct icon={Send} label="Send" verbLabel />
        </div>
      </div>
    )
  }

  /* ── Instagram ─────────────────────────── Figma 28673-2118 (440×540) */
  if (network === "instagram") {
    const handle = post.handle?.replace(/^@/, "") ?? post.author.toLowerCase()
    return (
      <div className="tw:flex tw:w-full tw:flex-col tw:items-start tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:bg-card">
        <div className="tw:flex tw:w-full tw:items-center tw:gap-2 tw:px-3 tw:py-2.5">
          <PostAvatar post={post} size="tw:size-8" text="tw:text-xs" />
          <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-[13px] tw:leading-[18px] tw:font-semibold tw:text-foreground">
            {handle}
          </span>
          <Ellipsis className="tw:size-4 tw:shrink-0 tw:text-foreground" />
        </div>

        {/* Full-bleed, unrounded — the media is the card.
            aspect-square, not Figma's h-[416px]: the media is full-bleed, so a
            fixed height meant the ratio moved with the viewport — 440x416 (~1:1)
            at the design width became 303x416 (0.73:1, portrait) on a phone,
            showing a shape Instagram never renders on the one screen whose job
            is to predict the post. A ratio holds at every width; the cost is
            440 tall instead of 416 at the design width. */}
        <PostMedia
          src={post.mediaUrl}
          videoSrc={post.videoUrl}
          className="tw:aspect-square"
        />

        {/* Bookmark is pushed to the right edge, as Instagram renders it. Figma
            28673-2118 drew it clustered with the others behind a 10px spacer;
            this follows the live UI instead, at every width. */}
        <div className="tw:flex tw:w-full tw:items-center tw:gap-4 tw:px-3 tw:pt-2.5 tw:pb-1.5 tw:text-foreground">
          <Heart className="tw:size-6" />
          <MessageCircle className="tw:size-6" />
          <Send className="tw:size-6" />
          <Bookmark className="tw:ml-auto tw:size-6" />
        </div>

        <div className="tw:w-full tw:px-3 tw:pb-3">
          <p className="tw:text-sm tw:leading-5 tw:text-foreground">
            <span className="tw:font-semibold">{handle}</span>{" "}
            {post.headline ?? post.body}
          </p>
        </div>
      </div>
    )
  }

  /* ── Facebook (and the fallback) ───────── Figma 28671-2082 (440×409) */
  return (
    <div className={POST_SHELL}>
      <div className="tw:flex tw:w-full tw:items-center tw:gap-2">
        <PostAvatar post={post} size="tw:size-10" text="tw:text-sm" />
        <div className="tw:flex tw:min-w-0 tw:flex-col">
          <span className="tw:truncate tw:text-sm tw:leading-[18px] tw:font-semibold tw:text-foreground">
            {post.author}
          </span>
          <span className="tw:truncate tw:text-xs tw:leading-4 tw:text-muted-foreground">
            {[time, post.audience].filter(Boolean).join(" · ")}
          </span>
        </div>
      </div>

      <p className="tw:w-full tw:text-sm tw:leading-5 tw:text-foreground">
        {post.body}
      </p>

      <PostLinkCard post={post} variant="facebook" />

      <div className="tw:h-px tw:w-full tw:bg-border" />

      {/* px-2 at mobile: Facebook's three labels do fit, but 24px of side
          padding each way left only ~24px between them. Labels stay — three is
          comfortable at 263px where LinkedIn's four are not. */}
      <div className="tw:flex tw:w-full tw:items-start tw:justify-between tw:px-2 tw:pt-0.5 tw:sm:px-6">
        <PostAct icon={ThumbsUp} label="Like" />
        <PostAct icon={MessageCircle} label="Comment" />
        <PostAct icon={Share} label="Share" />
      </div>
    </div>
  )
}

/**
 * Stand-in while the source's most recent item is still being fetched. Sized off
 * the shortest real card (X, 351px at the design width) so the step doesn't jump
 * height when the content lands.
 */
function PostCardSkeleton() {
  return (
    <div className={POST_SHELL} aria-hidden>
      <div className="tw:flex tw:w-full tw:items-center tw:gap-2">
        <div className="tw:size-10 tw:shrink-0 tw:animate-pulse tw:rounded-full tw:bg-muted" />
        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-1.5">
          <div className="tw:h-3.5 tw:w-1/3 tw:animate-pulse tw:rounded-xs tw:bg-muted" />
          <div className="tw:h-3 tw:w-1/5 tw:animate-pulse tw:rounded-xs tw:bg-muted" />
        </div>
      </div>
      <div className="tw:h-4 tw:w-4/5 tw:animate-pulse tw:rounded-xs tw:bg-muted" />
      <div className="tw:aspect-[19/10] tw:w-full tw:animate-pulse tw:rounded-lg tw:bg-muted" />
    </div>
  )
}

export function PostPreviewStep({
  source,
  base,
  networks,
  activeNetwork,
  post,
  loading,
  error,
  onNetworkChange,
}: {
  source: ConnectedSource
  base: string
  networks: NetworkSlug[]
  activeNetwork: NetworkSlug
  post: PostPreviewSpec
  /** Renders a skeleton card instead of the post. */
  loading?: boolean
  /**
   * Replaces the card with a failure message. Only for a preview that could not
   * be fetched — a source with nothing published yet is not an error, and the
   * caller says so through `post.note` instead.
   */
  error?: string
  onNetworkChange?: (network: NetworkSlug) => void
}) {
  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:gap-6 tw:md:gap-8">
      <SourceChip source={source} base={base} />

      <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-[440px] tw:flex-col tw:gap-4">
        <Tabs
          value={activeNetwork}
          onValueChange={
            onNetworkChange
              ? (value) => onNetworkChange(value as NetworkSlug)
              : undefined
          }
          className="tw:w-full"
        >
          {/* Scrolls rather than shrinking. `networks` is a prop, and a squeezed
              row of network icons stops being recognisable well before it stops
              fitting — the design's four never reach that point, but nothing
              here guarantees a caller passes four. */}
          <TabsList
            variant="line"
            className="tw:w-full tw:justify-start tw:overflow-x-auto tw:rounded-none tw:border-b tw:border-border"
          >
            {networks.map((network) => (
              <TabsTrigger
                key={network}
                value={network}
                className="tw:flex-none"
                aria-label={NETWORKS[network].label}
              >
                {/* The solid variant — a glyph from the `dlvr-networks` icon
                    font — in one flat grey rather than each network's brand
                    colour. Eight saturated marks in a 24px row competed with the
                    post card they label, and the brand hues are already carried
                    by the badges on the destinations step.

                    Named by its font rather than its CSS class prefix on
                    purpose: bin/networks/check-parity.mjs reserves that prefix
                    to the generator and its own files, and fails on any other
                    tracked file that so much as mentions it.

                    The glyph takes `currentColor`, so this `text-` class
                    overrides the trigger's own text-foreground/60 and the
                    inactive tabs need their own statement — opacity. The
                    underline still marks the active one. */}
                <Networks
                  name={network}
                  variant="solid"
                  size={TAB_GLYPH_SIZE[network] ?? TAB_GLYPH_DEFAULT}
                  assetBaseUrl={base}
                  className={cn(
                    "tw:text-gray-500 tw:transition-opacity",
                    network === activeNetwork
                      ? "tw:opacity-100"
                      : "tw:opacity-60"
                  )}
                />
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {error ? (
          <div className="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:border-border tw:py-10 tw:text-center">
            <TriangleAlert className="tw:size-6 tw:text-destructive" />
            <p className="tw:text-sm tw:font-medium tw:text-foreground">
              {error}
            </p>
            <p className="tw:max-w-[280px] tw:text-sm tw:text-balance tw:text-muted-foreground">
              {`Your automation will still post — we just couldn't reach the source for a sample.`}
            </p>
          </div>
        ) : loading ? (
          <PostCardSkeleton />
        ) : (
          <PostCard network={activeNetwork} post={post} />
        )}

        {post.note && !loading && !error ? (
          <p className={cn("tw:flex tw:items-center tw:justify-center tw:gap-1.5", HELP_TEXT)}>
            <Info className="tw:size-3.5 tw:shrink-0" />
            {post.note}
          </p>
        ) : null}
      </div>
    </div>
  )
}
